/**
 * Lógica pura de resize de colunas, compartilhada entre as variantes do List
 * (list/index.jsx, list/tree.jsx, NewList) para não perpetuar cópias divergentes
 * da mesma fórmula. Não depende de React nem de nenhum estado de instância —
 * cada variante é responsável por conectar isso ao seu próprio ciclo de mouse
 * (listeners, refs, setState/hooks).
 */

export const MIN_COLUMN_WIDTH = 60;

/**
 * Calcula a nova largura (em px) a partir do deslocamento do mouse desde o
 * início do arraste, respeitando o mínimo efetivo da coluna.
 */
export function computeResizedWidth({
  startWidth,
  startX,
  clientX,
  minWidth,
  scale = 1,
}) {
  // `scale` compensa telas com CSS `zoom`: o mouse se move em pixels de tela, mas a
  // largura é definida em pixels CSS do elemento (que a tela multiplica por `zoom`).
  const delta = (clientX - startX) / scale;
  return Math.max(minWidth, startWidth + delta);
}

/**
 * Mede a largura mínima necessária para `headerCell` exibir todo o seu conteúdo em uma
 * linha só (texto do cabeçalho + ícone de ordenação, se houver), sem cortar nem
 * sobrepor a coluna seguinte — é esse valor que deve virar o piso do resize.
 *
 * A medição é feita num "probe" fora da tabela: o conteúdo do cabeçalho é clonado para um
 * elemento escondido, com `width: max-content` e `white-space: nowrap`, e a largura dele
 * mais o padding/borda do <th> é o resultado. Não dá para ler a largura do próprio <th>
 * (nem forçando `width: auto`): dentro de uma tabela a largura da coluna é decidida pelo
 * conteúdo das células do CORPO (inclusive por min-widths padrão delas), então a medição
 * devolveria o tamanho dessas células e não o do texto do cabeçalho — foi isso que
 * travava o resize antes de chegar na largura do título.
 *
 * Filhos absolutamente posicionados (a alça de resize) ficam de fora.
 *
 * O probe fica no <body>, fora de qualquer contêiner com CSS `zoom` (ex.: Card de Metas),
 * então o resultado já vem em pixels CSS do elemento; `scale` é mantido só por
 * compatibilidade com os chamadores.
 */
// eslint-disable-next-line no-unused-vars
export function measureHeaderMinWidth(headerCell, { scale = 1, handleWidth = 8 } = {}) {
  if (!headerCell || typeof document === 'undefined') return 0;

  const computed = window.getComputedStyle(headerCell);
  const probe = document.createElement('div');

  probe.style.cssText =
    'position:absolute;visibility:hidden;pointer-events:none;left:-99999px;top:0;' +
    'width:max-content;max-width:none;white-space:nowrap;';
  ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'textTransform'].forEach(
    (prop) => {
      probe.style[prop] = computed[prop];
    }
  );

  Array.from(headerCell.childNodes).forEach((node) => {
    if (
      node.nodeType === 1 &&
      window.getComputedStyle(node).position === 'absolute'
    )
      return;

    probe.appendChild(node.cloneNode(true));
  });

  document.body.appendChild(probe);
  const content = probe.getBoundingClientRect().width;
  document.body.removeChild(probe);

  const spacing =
    (parseFloat(computed.paddingLeft) || 0) +
    (parseFloat(computed.paddingRight) || 0) +
    (parseFloat(computed.borderLeftWidth) || 0) +
    (parseFloat(computed.borderRightWidth) || 0);

  return Math.ceil(content + spacing) + handleWidth;
}

/**
 * Retorna uma nova lista com a coluna de `valueField` correspondente substituída
 * por um clone com a largura atualizada e `widthPersisted: true`. As demais
 * colunas mantêm a mesma referência (sem clonar o array inteiro à toa).
 *
 * Usado tanto para o commit otimista no estado quanto, potencialmente, para
 * qualquer outro lugar que precise aplicar a mesma largura a uma lista de colunas.
 */
export function withUpdatedColumnWidth(list, valueField, width) {
  return (list || []).map((c) =>
    c && c.valueField === valueField ? { ...c, width, widthPersisted: true } : c
  );
}

/**
 * Monta o payload de /ColunasTabela/SalvarTamanhoColunas a partir das colunas
 * atualmente exibidas.
 *
 * Por que enviar TODAS as colunas exibidas e não só a redimensionada: se o usuário
 * ainda não tem configuração salva (está vendo as colunas padrão), o backend cria a
 * configuração a partir do que recebe. Enviar só a coluna redimensionada faria a
 * configuração nascer com uma única coluna, e no próximo carregamento a lista
 * exibiria só ela. Enviando o conjunto completo, a configuração criada é idêntica ao
 * que o usuário já está vendo.
 *
 * Colunas sem largura persistida vão sem `width`, o que não altera nada no servidor
 * (elas também não têm largura salva lá).
 */
export function buildWidthPayload(displayedColumns) {
  return (displayedColumns || [])
    .filter((c) => c && c.visible && c.valueField)
    .map((c) => ({
      // headerText pode ser um nó React em algumas telas; só strings são serializáveis.
      headerText: typeof c.headerText === 'string' ? c.headerText : c.valueField,
      valueField: c.valueField,
      width: c.widthPersisted ? c.width : undefined,
    }));
}
